/**
 * @file return-order.service.ts
 * @module orders-delivery/services/return-order
 * @description API service for informing the backend of a delivered order's returned goods.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.6
 * @version 2.1.2
 */

import APICallService from '../../../../api/apiCallService';
import { orderReturn } from '../../../../api/apiEndPoints';
import { orderDeliveryJSON } from '../../../../api/apiJSON/order';
import { OrdersDelivery, ReceivedDirectGood } from '../../../../utils/constants';

/**
 * Informs the backend of returned goods for a delivered order/vehicle.
 *
 * @param {string} orderId - Order ID to submit the return for.
 * @param {{ route: string; vehicle: string; variants: any[] }} payload - Route, vehicle, and returned variants.
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function submitOrderReturn(
  orderId: string,
  payload: { route: string; vehicle: string; variants: any[] },
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    orderReturn.inform,
    orderDeliveryJSON.returnInform(payload),
    { _id: orderId },
    '',
    '',
    '',
    ReceivedDirectGood,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Fetches the draft returned-goods list for an order/vehicle, used to adjust return batches.
 *
 * @param {string} orderId - Order ID to fetch the draft return list for.
 * @param {{ route: string; vehicle: string; warehouse: string | null }} payload - Route, vehicle, and warehouse.
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function fetchOrderReturnDraft(
  orderId: string,
  payload: { route: string; vehicle: string; warehouse: string | null },
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    orderReturn.list,
    orderDeliveryJSON.returnList(payload),
    { _id: orderId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Submits adjusted return batches (variant/batch/quantity/reason) for an order/vehicle.
 *
 * @param {string} orderId - Order ID to submit the adjustment for.
 * @param {{ route: string; vehicle: string; variants: any[]; scheduleForNextDay: boolean }} payload - Route, vehicle, adjusted variants, and scheduling flag.
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function submitOrderReturnAdjustment(
  orderId: string,
  payload: { route: string; vehicle: string; variants: any[]; scheduleForNextDay: boolean },
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    orderReturn.stockAdjust,
    orderDeliveryJSON.returnAdjust(payload),
    { _id: orderId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}
